import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Kinds that represent deployable / documentable software catalog entities.
 * Excludes User, Group, Location, and other org/metadata kinds.
 */
const ENTITY_PICKER_KINDS = [
  'Component',
  'API',
  'Resource',
  'System',
  'Domain',
  'Template',
] as const;

/**
 * OR-of-kinds filter: each `{ kind: 'X' }` becomes its own `filter` query param.
 * @see CatalogClient.getFilterValue — outer array is anyOf.
 */
export const ENTITY_PICKER_CATALOG_FILTER = ENTITY_PICKER_KINDS.map(kind => ({
  kind,
}));

/**
 * Fields used for catalog full-text search. Without this list, the backend
 * defaults to `metadata.uid` only, which breaks searching by entity name.
 */
export const ENTITY_PICKER_FULL_TEXT_FIELDS = [
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.uid',
] as const;

/**
 * Prefer name/title prefix matches, then ref matches, so typing behaves like
 * a typical "starts with" picker even though SQL uses `%term%` substring.
 */
export function rankEntitySearchResults(
  entities: Entity[],
  term: string,
): Entity[] {
  const list = entities ?? [];
  const q = term.trim().toLowerCase();
  if (!q) {
    return list;
  }

  const score = (e: Entity): number => {
    const name = (e.metadata.name ?? '').toLowerCase();
    const title = (e.metadata.title ?? '').toLowerCase();
    const ns = (e.metadata.namespace ?? '').toLowerCase();
    const ref = stringifyEntityRef(e).toLowerCase();
    const compound = `${ns}/${name}`;

    if (name.startsWith(q)) {
      return 0;
    }
    if (title.startsWith(q)) {
      return 1;
    }
    if (compound.startsWith(q) || ref.startsWith(q) || ns.startsWith(q)) {
      return 2;
    }
    if (
      name.includes(q) ||
      title.includes(q) ||
      ns.includes(q) ||
      ref.includes(q)
    ) {
      return 3;
    }
    return 4;
  };

  return [...list].sort((a, b) => {
    const diff = score(a) - score(b);
    if (diff !== 0) {
      return diff;
    }
    return stringifyEntityRef(a).localeCompare(stringifyEntityRef(b));
  });
}
